// materialsCalc.ts — Concrete volume, weight, CG, area & embed load calculator
// Mirrors the logic from v11 panatt.lsp (panel attributes / materials list)
// All inputs in inches; internal volume in cubic inches.
//
// CG uses 2D centroid with through-opening subtraction (uniform density assumption).
// Does NOT factor in weight offset from rebar placement.

import type { PanelData } from '../types/panelvar';
import { outlineFromMp, computePanelArea, computePanelCentroid, computeDiagonals } from './panelGeometry';

export interface MaterialsResult {
  panelId:          string;
  mark:             string;

  // ── Volume ──────────────────────────────────────────────────────────────
  grossVolCuIn:     number;  // panel W × H × T before subtracting openings
  openingVolCuIn:   number;  // total volume removed by openings + feature recesses
  concreteVolCuIn:  number;  // gross - openings
  concreteCuYd:     number;  // concreteVolCuIn / 1728 / 27
  concreteCuM:      number;  // cubic metres (concreteCuYd × 0.764555)

  // ── Weight ──────────────────────────────────────────────────────────────
  panelWeightLb:    number;  // concreteVolCuIn / 1728 × density pcf
  panelWeightKips:  number;  // panelWeightLb / 1000
  panelWeightTons:  number;  // panelWeightLb / 2000 (short tons)
  panelWeightKg:    number;  // panelWeightLb × 0.453592

  // ── Area ────────────────────────────────────────────────────────────────
  formAreaSqFt:     number;  // gross face W×H / 144 (for form/mold estimation)
  formAreaSqM:      number;  // formAreaSqFt × 0.092903
  exteriorFaceAreaSqFt: number; // gross face INCLUDING openings = same as formAreaSqFt
  finishedAreaSqFt: number;  // gross - through-opening areas - feature strip areas (paintable/sealable)
  finishedAreaSqM:  number;  // finishedAreaSqM × 0.092903

  // ── Center of gravity (from panel bottom-left 0,0 in inches) ───────────
  cgX:              number;  // 2D centroid X adjusted for through-openings
  cgY:              number;  // 2D centroid Y adjusted for through-openings

  // ── Structural ratios ──────────────────────────────────────────────────
  slendernessRatio: number;  // H / T (unitless)

  // ── Per-embed loads ────────────────────────────────────────────────────
  craneLoadPerPickLb:   number;  // panelWeightLb / pickCount (0 if no picks)
  braceLoadPerPointLb:  number;  // panelWeightLb / braceCount (0 if no braces)

  // ── Counts ──────────────────────────────────────────────────────────────
  braceCount:       number;
  pickCount:        number;
  weldCount:        number;
  openingCount:     number;

  // ── Diagonals (for squaring) ───────────────────────────────────────────
  diagonal1In:      number;  // BL→TR = √(W² + hR²) inches
  diagonal2In:      number;  // BR→TL = √(W² + hL²) inches
  diagonalsMatch:   boolean; // |D1 - D2| < 1/8"
}

const CONCRETE_PCF = 150;  // lb per cubic foot (normal weight concrete)
const CU_YD_TO_CU_M = 0.764555;
const SQ_FT_TO_SQ_M = 0.092903;
const LB_TO_KG       = 0.453592;

export function calcMaterials(panel: PanelData): MaterialsResult {
  const mp = panel.mpvar;
  const outline = outlineFromMp(mp);
  const grossFaceAreaSqIn = computePanelArea(outline);
  const grossVol = grossFaceAreaSqIn * mp.thickness;

  let openingVol = 0;
  let openingCount = 0;

  // ── Through-opening tracking for CG + area subtraction ─────────────────
  // Each entry: [area_sq_in, centroid_x, centroid_y]
  const openingCentroids: [number, number, number][] = [];
  const ffeY = -(mp.botElev ?? 0) * 12;

  const addRectOpening = (cx: number, cy: number, w: number, h: number) => {
    const area = w * h;
    openingVol += area * mp.thickness;
    openingCount++;
    openingCentroids.push([area, cx, cy]);
  };

  panel.rovar.forEach(s => {
    if (!s.enabled) return;
    addRectOpening(s.data.x, s.data.y + ffeY, s.data.width, s.data.height);
  });
  panel.wdvar.forEach(s => {
    if (!s.enabled) return;
    addRectOpening(s.data.x, s.data.y + ffeY, s.data.width, s.data.height);
  });
  panel.drvar.forEach(s => {
    if (!s.enabled) return;
    // drvar y is bottom of door; centroid = y + height/2
    addRectOpening(s.data.x, s.data.y + ffeY + s.data.height / 2, s.data.width, s.data.height);
  });
  panel.dlvar.forEach(s => {
    if (!s.enabled) return;
    addRectOpening(s.data.x, s.data.y + ffeY, s.data.width, s.data.height);
  });
  panel.sbvar.forEach(s => {
    if (!s.enabled) return;
    addRectOpening(s.data.x, s.data.y + ffeY, s.data.width, s.data.height);
  });
  panel.rbvar.forEach(s => {
    if (!s.enabled) return;
    const r = s.data.diameter / 2;
    const area = Math.PI * r * r;
    openingVol += area * mp.thickness;
    openingCount++;
    openingCentroids.push([area, s.data.x, s.data.y + ffeY]);
  });

  // Through-opening 2D area sum (for face area subtraction)
  const throughOpeningAreaSqIn = openingCentroids.reduce((a, [area]) => a + area, 0);

  // ── Feature strip volume (shallow recesses, not through-openings) ──────
  let featureAreaSqIn = 0;

  panel.fhvar.forEach(s => {
    if (!s.enabled) return;
    const xS = s.data.xEnd > 0 ? s.data.xStart : 0;
    const xE = s.data.xEnd > 0 ? s.data.xEnd   : mp.width;
    const fw = xE - xS;
    openingVol += fw * s.data.height * (s.data.depth ?? 0.75);
    featureAreaSqIn += fw * s.data.height;
  });
  panel.fvvar.forEach(s => {
    if (!s.enabled) return;
    const yS = s.data.yEnd > 0 ? s.data.yStart : 0;
    const yE = s.data.yEnd > 0 ? s.data.yEnd   : mp.height;
    const fh = yE - yS;
    openingVol += s.data.width * fh * (s.data.depth ?? 0.75);
    featureAreaSqIn += s.data.width * fh;
  });

  // ── Volume & weight ────────────────────────────────────────────────────
  const concreteVolCuIn = Math.max(0, grossVol - openingVol);
  const concreteCuFt    = concreteVolCuIn / 1728;
  const concreteCuYd    = concreteCuFt / 27;
  const concreteCuM     = concreteCuYd * CU_YD_TO_CU_M;
  const panelWeightLb   = concreteCuFt * CONCRETE_PCF;
  const panelWeightKips = panelWeightLb / 1000;
  const panelWeightTons = panelWeightLb / 2000;
  const panelWeightKg   = panelWeightLb * LB_TO_KG;

  // ── Area ───────────────────────────────────────────────────────────────
  const formAreaSqFt    = grossFaceAreaSqIn / 144;
  const formAreaSqM     = formAreaSqFt * SQ_FT_TO_SQ_M;
  const exteriorFaceAreaSqFt = formAreaSqFt;  // gross face, includes where openings are
  const finishedAreaSqFt = Math.max(0, (grossFaceAreaSqIn - throughOpeningAreaSqIn - featureAreaSqIn) / 144);
  const finishedAreaSqM  = finishedAreaSqFt * SQ_FT_TO_SQ_M;

  // ── Center of gravity (2D centroid with through-opening subtraction) ───
  // Uses polygon centroid for non-rectangular panel shapes.
  // Coords: 0,0 = panel bottom-left corner; units = inches.
  const grossArea = grossFaceAreaSqIn;
  const netArea   = grossArea - throughOpeningAreaSqIn;

  const outlineCG = computePanelCentroid(outline);
  let cgX = outlineCG.x;
  let cgY = outlineCG.y;
  if (netArea > 0 && openingCentroids.length > 0) {
    let momentX = grossArea * outlineCG.x;
    let momentY = grossArea * outlineCG.y;
    for (const [area, cx, cy] of openingCentroids) {
      momentX -= area * cx;
      momentY -= area * cy;
    }
    cgX = momentX / netArea;
    cgY = momentY / netArea;
  }

  // ── Structural ratios ─────────────────────────────────────────────────
  const slendernessRatio = mp.thickness > 0 ? mp.height / mp.thickness : 0;

  // ── Embed counts + per-embed loads ────────────────────────────────────
  const braceCount = panel.bpvar.filter(s => s.enabled).length;
  const pickCount  = panel.ppvar.filter(s => s.enabled).length;
  const weldCount  = panel.wcvar.filter(s => s.enabled).length;

  const craneLoadPerPickLb  = pickCount  > 0 ? panelWeightLb / pickCount  : 0;
  const braceLoadPerPointLb = braceCount > 0 ? panelWeightLb / braceCount : 0;

  // ── Diagonals (for squaring) ──────────────────────────────────────────
  // Uses actual outline corner heights (supports trapezoidal/gable panels)
  const diags = computeDiagonals(outline);
  const diagonal1In    = diags.d1;
  const diagonal2In    = diags.d2;
  const diagonalsMatch = diags.match;

  return {
    panelId:         panel.id,
    mark:            mp.panelMark,
    grossVolCuIn:    grossVol,
    openingVolCuIn:  openingVol,
    concreteVolCuIn,
    concreteCuYd,
    concreteCuM,
    panelWeightLb,
    panelWeightKips,
    panelWeightTons,
    panelWeightKg,
    formAreaSqFt,
    formAreaSqM,
    exteriorFaceAreaSqFt,
    finishedAreaSqFt,
    finishedAreaSqM,
    cgX,
    cgY,
    slendernessRatio,
    craneLoadPerPickLb,
    braceLoadPerPointLb,
    braceCount,
    pickCount,
    weldCount,
    openingCount,
    diagonal1In,
    diagonal2In,
    diagonalsMatch,
  };
}

export function calcProjectTotals(results: MaterialsResult[]) {
  return {
    totalCuYd:         results.reduce((a, r) => a + r.concreteCuYd,   0),
    totalCuM:          results.reduce((a, r) => a + r.concreteCuM,    0),
    totalWeightLb:     results.reduce((a, r) => a + r.panelWeightLb,  0),
    totalWeightTon:    results.reduce((a, r) => a + r.panelWeightLb,  0) / 2000,
    totalWeightKg:     results.reduce((a, r) => a + r.panelWeightKg,  0),
    totalFormSqFt:     results.reduce((a, r) => a + r.formAreaSqFt,   0),
    totalFormSqM:      results.reduce((a, r) => a + r.formAreaSqM,    0),
    totalFinishedSqFt: results.reduce((a, r) => a + r.finishedAreaSqFt, 0),
    totalFinishedSqM:  results.reduce((a, r) => a + r.finishedAreaSqM,  0),
    totalBraces:       results.reduce((a, r) => a + r.braceCount,      0),
    totalPicks:        results.reduce((a, r) => a + r.pickCount,       0),
    totalWelds:        results.reduce((a, r) => a + r.weldCount,       0),
    totalOpenings:     results.reduce((a, r) => a + r.openingCount,    0),
  };
}

// ── Architectural scale computation ──────────────────────────────────────────
// Given a panel's dimensions (inches) and a paper size, compute the arch scale
// that would fit the panel onto that sheet.

type PaperSize = 'ARCH-D' | 'ARCH-E' | 'ANSI-D' | 'ANSI-E' | 'A1' | 'A0' | 'Letter' | 'Legal' | 'Tabloid';

// Full sheet dimensions in inches, landscape orientation: [width, height]
export const PAPER_SHEET_DIMS: Record<string, [number, number]> = {
  'ARCH-D':  [36, 24],
  'ARCH-E':  [48, 36],
  'ANSI-D':  [34, 22],
  'ANSI-E':  [44, 34],
  'A1':      [33.1, 23.4],  // 841×594mm
  'A0':      [46.8, 33.1],  // 1189×841mm
  'Letter':  [11, 8.5],
  'Legal':   [14, 8.5],
  'Tabloid': [17, 11],
};

// Printable area in inches (sheet minus 1" margins each side)
const PAPER_PRINT_AREAS: Record<PaperSize, [number, number]> = {
  'ARCH-D':  [22, 34],
  'ARCH-E':  [34, 46],
  'ANSI-D':  [20, 32],
  'ANSI-E':  [32, 42],
  'A1':      [21.4, 31.1],  // 594×841mm → inches, minus margins
  'A0':      [31.1, 44.8],  // 841×1189mm → inches, minus margins
  'Letter':  [6.5, 9],
  'Legal':   [6.5, 12],
  'Tabloid': [9, 15],
};

// Standard arch scales: inches-on-paper per foot-of-real
const ARCH_SCALES: { inPerFt: number; label: string }[] = [
  { inPerFt: 3,     label: '3" = 1\'-0"' },
  { inPerFt: 1.5,   label: '1½" = 1\'-0"' },
  { inPerFt: 1,     label: '1" = 1\'-0"' },
  { inPerFt: 0.75,  label: '¾" = 1\'-0"' },
  { inPerFt: 0.5,   label: '½" = 1\'-0"' },
  { inPerFt: 0.375, label: '⅜" = 1\'-0"' },
  { inPerFt: 0.25,  label: '¼" = 1\'-0"' },
  { inPerFt: 3/16,  label: '3/16" = 1\'-0"' },
  { inPerFt: 0.125, label: '⅛" = 1\'-0"' },
  { inPerFt: 3/32,  label: '3/32" = 1\'-0"' },
  { inPerFt: 1/16,  label: '1/16" = 1\'-0"' },
];

/**
 * Compute the architectural drawing scale for a panel on a given paper size.
 * Returns the scale label string (e.g., '⅜" = 1\'-0"').
 * Panel is oriented with width along paper-long, height along paper-short (minus ~4" for title block).
 */
export function computeArchScale(panelWidthIn: number, panelHeightIn: number, paperSize: string): string {
  const area = PAPER_PRINT_AREAS[paperSize as PaperSize];
  if (!area) return 'N/A';
  const [printShort, printLong] = area;
  // Reserve 4" on short side for title block
  const availShort = printShort - 4;
  const availLong  = printLong;

  const panelWFt = panelWidthIn / 12;
  const panelHFt = panelHeightIn / 12;
  if (panelWFt <= 0 || panelHFt <= 0) return 'N/A';

  // Max scale that fits: min(paperLong / panelW_ft, paperShort / panelH_ft)
  const maxInPerFt = Math.min(availLong / panelWFt, availShort / panelHFt);

  // Find largest standard scale that fits
  for (const s of ARCH_SCALES) {
    if (s.inPerFt <= maxInPerFt) return s.label;
  }
  return '< 1/16" = 1\'-0"';
}
